import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, CircularProgress, Alert, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import apiClient from '../../config/axios';
import TeacherDetailsInfo from './TeacherDetailsInfo';
import TeacherDetailsCourses from './TeacherDetailsCourses';
import TeacherDetailsSalary from './TeacherDetailsSalary';
import TeacherDetailsTransfers from './TeacherDetailsTransfers';
import TeacherDetailsExtra from './TeacherDetailsExtra';
import TeacherDetailsSummary from './TeacherDetailsSummary';

const TeacherDetailsPage = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();

  const [teacher, setTeacher] = useState(null);
  const [coursesData, setCoursesData] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [otherCompensations, setOtherCompensations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTeacherDetails = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch teacher details
      const foundTeacher = await apiClient.get(`/api/user/${teacherId}`);
      if (!foundTeacher.data) {
        setError('Docente non trovato');
        return;
      }

      setTeacher(foundTeacher.data);

      // Fetch teacher courses with stats
      try {
        const coursesRes = await apiClient.get(`/api/teacher/${teacherId}/courses`);
        setCoursesData(coursesRes.data);
      } catch (err) {
        console.error('Error loading courses:', err);
        setCoursesData({ courses: [], grandTotal: 0 });
      }

      // Fetch teacher transfers
      try {
        const transfersRes = await apiClient.get(`/api/teacher/${teacherId}/transfers`);
        setTransfers(transfersRes.data || []);
      } catch (err) {
        console.error('Error loading transfers:', err);
        setTransfers([]);
      }

      // Fetch teacher other compensations
      try {
        const compRes = await apiClient.get(`/api/teacher/${teacherId}/other-compensations`);
        setOtherCompensations(compRes.data || []);
      } catch (err) {
        console.error('Error loading other compensations:', err);
        setOtherCompensations([]);
      }
    } catch (err) {
      console.error('Error loading teacher details:', err);
      setError('Errore nel caricamento dei dettagli docente');
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  useEffect(() => {
    fetchTeacherDetails();
  }, [fetchTeacherDetails]);

  if (loading) {
    return (
      <Container maxWidth='xl' sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth='xl' sx={{ mt: 4, mb: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Torna indietro
        </Button>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!teacher) {
    return (
      <Container maxWidth='xl' sx={{ mt: 4, mb: 4 }}>
        <Alert severity='warning' sx={{ mb: 2 }}>
          Docente non trovato
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Torna indietro
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TeacherDetailsInfo teacher={teacher} />
        <TeacherDetailsSalary teacher={teacher} onUpdate={fetchTeacherDetails} />
        <TeacherDetailsCourses coursesData={coursesData} teacher={teacher} />
        <TeacherDetailsExtra teacherId={teacherId} otherCompensations={otherCompensations} onUpdate={fetchTeacherDetails} />
        <TeacherDetailsTransfers teacherId={teacherId} transfers={transfers} onUpdate={fetchTeacherDetails} />
        <TeacherDetailsSummary coursesData={coursesData} transfers={transfers} otherCompensations={otherCompensations} teacher={teacher} />
      </Box>
    </Container>
  );
};

export default TeacherDetailsPage;
